/**
 * Iter122.7 — Reason-code renderer, Shadow banner, and Shadow toggle
 * ===================================================================
 * Presentation-only React components used across the GEX v2 shadow-mode
 * surfaces (SettingsPage opt-in, MarketForge cockpit banner, /shadow
 * diagnostics workspace).  All copy is non-deterministic — the labels
 * come from the backend's reason-code catalog itself.
 *
 * Exports
 *   <ReasonCodeChip rendered={...} />
 *   <ReasonCodeList rendered={[...]} />
 *   <ShadowBanner />          — renders when status.shadow_enabled
 *   <ShadowFallbackBanner reason="…" /> — renders when engine_used
 *                                          reverts to v1 mid-request
 *   <ShadowToggle />          — opt-in switch used in Settings
 *   <useShadowStatus>()       — hook returning current status
 *
 * v1 stays the production default.  These components never mutate v1
 * output; they render additive banners + chips on top of it.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Category → color/severity map. Kept deliberately non-deterministic —
// every chip explicitly labels itself as "possible", "modeled", or
// "informational" via the copy in `message`.
// ---------------------------------------------------------------------------

const CATEGORY_STYLE = {
  market_state:      { bg: "#1f2937", fg: "#e5e7eb", severity: "info"    },
  path_status:       { bg: "#0b3d59", fg: "#c7ecff", severity: "info"    },
  wall_usage:        { bg: "#3f2f0e", fg: "#ffd18a", severity: "caution" },
  zg_usage:          { bg: "#3f2f0e", fg: "#ffd18a", severity: "caution" },
  confidence:        { bg: "#3b1616", fg: "#fecaca", severity: "warning" },
  deprecation:       { bg: "#3b1616", fg: "#fecaca", severity: "warning" },
  trade_modifier:    { bg: "#12351d", fg: "#c7f7d0", severity: "info"    },
  trade_gate:        { bg: "#3b1616", fg: "#fecaca", severity: "blocked" },
  trade_status:      { bg: "#1f2937", fg: "#e5e7eb", severity: "info"    },
  wes_modifier:      { bg: "#22222f", fg: "#e2e2f0", severity: "info"    },
  wes_data:          { bg: "#3f2f0e", fg: "#ffd18a", severity: "caution" },
  unknown:           { bg: "#2a2a2a", fg: "#dddddd", severity: "info"    },
};

function chipStyle(category) {
  const s = CATEGORY_STYLE[category] || CATEGORY_STYLE.unknown;
  return {
    backgroundColor: s.bg,
    color:           s.fg,
    padding:         "4px 10px",
    borderRadius:    "9999px",
    fontSize:        "12px",
    fontWeight:      500,
    display:         "inline-flex",
    alignItems:      "center",
    gap:             "6px",
    cursor:          "help",
    border:          "1px solid rgba(255,255,255,0.08)",
    marginRight:     "6px",
    marginBottom:    "6px",
    letterSpacing:   "0.01em",
    fontFamily:      "ui-sans-serif, system-ui, -apple-system",
  };
}

function severityBadgeStyle(severity) {
  const bg = {
    info:    "#334155",
    caution: "#78500a",
    warning: "#7f1d1d",
    blocked: "#450a0a",
  }[severity] || "#334155";
  return {
    backgroundColor: bg,
    color:           "#fff",
    padding:         "1px 6px",
    borderRadius:    "4px",
    fontSize:        "10px",
    fontWeight:      600,
    textTransform:   "uppercase",
    letterSpacing:   "0.06em",
    lineHeight:      "12px",
  };
}

// ---------------------------------------------------------------------------
// <ReasonCodeChip />
// ---------------------------------------------------------------------------

export function ReasonCodeChip({ rendered, showAudit = false }) {
  const [open, setOpen] = useState(false);
  if (!rendered) return null;

  const category      = rendered.category || "unknown";
  const label         = rendered.label || rendered.code;
  const message       = rendered.message || "";
  const invalidation  = rendered.invalidation || null;
  const usageStatus   = rendered?.extras?.usage_status || null;
  const confLimit     = rendered?.extras?.confidence_limitation || null;
  const severity      = (CATEGORY_STYLE[category] || CATEGORY_STYLE.unknown).severity;

  return (
    <span
      style={chipStyle(category)}
      data-testid={`reason-code-chip-${rendered.code}`}
      title={message}
      onClick={() => setOpen((o) => !o)}
    >
      <span style={severityBadgeStyle(severity)}>{severity}</span>
      <span>{label}</span>
      {open && (
        <span
          style={{
            display:      "block",
            marginTop:    "8px",
            width:        "260px",
            fontSize:     "11px",
            fontWeight:   400,
            lineHeight:   "1.4",
            whiteSpace:   "normal",
            background:   "rgba(0,0,0,0.4)",
            padding:      "8px",
            borderRadius: "6px",
          }}
        >
          {message && <div style={{ marginBottom: "4px" }}>{message}</div>}
          {usageStatus && (
            <div style={{ opacity: 0.85 }}>
              <b>Usage:</b> {usageStatus}
            </div>
          )}
          {confLimit && (
            <div style={{ opacity: 0.85 }}>
              <b>Confidence limitation:</b> {confLimit}
            </div>
          )}
          {invalidation && (
            <div
              data-testid={`reason-code-invalidation-${rendered.code}`}
              style={{ marginTop: "4px", opacity: 0.85 }}>
              <b>Invalidation:</b> {invalidation}
            </div>
          )}
          {showAudit && (
            <div style={{ marginTop: "4px", opacity: 0.55, fontFamily: "ui-monospace" }}>
              code: {rendered.code}
            </div>
          )}
        </span>
      )}
    </span>
  );
}

export function ReasonCodeList({ rendered = [], showAudit = false }) {
  if (!rendered || !rendered.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap" }}
         data-testid="reason-code-list">
      {rendered.map((r, i) => (
        <ReasonCodeChip
          key={`${r.code}-${i}`}
          rendered={r}
          showAudit={showAudit}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// useShadowStatus — shared hook for the shadow-mode surfaces.
// ---------------------------------------------------------------------------

export function useShadowStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/forge/gex/v2/shadow-status`);
      setStatus(r.data || null);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { status, loading, refresh };
}

// ---------------------------------------------------------------------------
// <ShadowBanner />
// Renders the mandatory "GEX v2 SHADOW — Validation Only" banner in the
// cockpit when a user has opted in.  Includes a Return-to-v1 action.
// ---------------------------------------------------------------------------

export function ShadowBanner({ compact = false }) {
  const { status, refresh } = useShadowStatus();
  const [busy, setBusy]     = useState(false);
  if (!status || !status.shadow_enabled) return null;

  const revertToV1 = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/forge/gex/v2/shadow-toggle`,
                        { engine_version: "v1" });
      await refresh();
      // Also force a soft-reload so cockpit re-fetches with v1.
      window.location.reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="gex-v2-shadow-banner"
      style={{
        background:   "linear-gradient(90deg,#7f1d1d 0%,#78500a 100%)",
        color:        "#fff",
        padding:      compact ? "8px 14px" : "12px 18px",
        display:      "flex",
        alignItems:   "center",
        justifyContent: "space-between",
        gap:          "12px",
        fontSize:     "13px",
        borderRadius: "4px",
        border:       "1px solid rgba(255,255,255,0.15)",
        fontFamily:   "ui-sans-serif, system-ui, -apple-system",
      }}
    >
      <div>
        <b style={{ letterSpacing: "0.02em" }} data-testid="shadow-banner-title">
          {status.banner_short || "GEX v2 SHADOW — Validation Only"}
        </b>
        <div style={{ marginTop: "3px", opacity: 0.92, fontSize: "11px" }}
              data-testid="shadow-banner-support">
          {status.banner_supporting_text ||
            "v1 remains the production default. Shadow outputs do not execute trades or override Wheel decisions."}
        </div>
      </div>
      <button
        data-testid="shadow-banner-revert-v1"
        onClick={revertToV1}
        disabled={busy}
        style={{
          background:   "rgba(0,0,0,0.35)",
          color:        "#fff",
          border:       "1px solid rgba(255,255,255,0.25)",
          borderRadius: "6px",
          padding:      "6px 12px",
          cursor:       busy ? "wait" : "pointer",
          fontSize:     "12px",
          fontWeight:   500,
          whiteSpace:   "nowrap",
        }}
      >
        {busy ? "Reverting…" : "Return to v1"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// <ShadowFallbackBanner />
// Rendered when the wrapper endpoint had to fall back from v2 to v1.
// ---------------------------------------------------------------------------

export function ShadowFallbackBanner({ reason }) {
  if (!reason) return null;
  return (
    <div
      data-testid="shadow-fallback-banner"
      style={{
        background:   "#3b1616",
        color:        "#fecaca",
        padding:      "8px 12px",
        borderRadius: "4px",
        border:       "1px solid rgba(254,202,202,0.25)",
        fontSize:     "12px",
        marginTop:    "6px",
        fontFamily:   "ui-sans-serif, system-ui, -apple-system",
      }}
    >
      <b>GEX v2 Shadow unavailable — displaying production v1.</b>
      <div style={{ opacity: 0.85, marginTop: "3px", fontFamily: "ui-monospace",
                     fontSize: "10px" }}>
        {reason}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// <ShadowToggle />
// Opt-in switch used from the SettingsPage "Experimental / Shadow Modes"
// section.  Enforces server-side eligibility gate — non-eligible users
// see a disabled control with a plain-language explanation.
// ---------------------------------------------------------------------------

export function ShadowToggle() {
  const { status, refresh, loading } = useShadowStatus();
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  const toggle = async () => {
    if (!status) return;
    setBusy(true); setErr(null);
    const next = status.shadow_enabled ? "v1" : "v2_shadow";
    try {
      await axios.post(`${API}/forge/gex/v2/shadow-toggle`,
                        { engine_version: next });
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to update shadow mode");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !status) {
    return (
      <div data-testid="shadow-toggle-loading"
            style={{ padding: "12px", fontSize: "12px", opacity: 0.7 }}>
        Loading…
      </div>
    );
  }
  const eligible = status.shadow_eligible;
  const enabled  = status.shadow_enabled;

  return (
    <div
      data-testid="shadow-toggle"
      style={{
        padding: "14px 16px",
        border:  "1px solid #26262B",
        background: "#121216",
        borderRadius: "4px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start",
                     justifyContent: "space-between", gap: "12px" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: "13px", fontWeight: 700, color: "#fff" }}>
            GEX v2 Shadow Mode
          </div>
          <div style={{ fontSize: "11px", color: "#8A8A93", marginTop: "3px",
                          lineHeight: 1.5 }}>
            v1 remains the production default. Enabling shadow overlays v2
            outputs alongside v1 for validation only — it does not execute
            trades or override Wheel decisions.
          </div>
        </div>
        <span
          data-testid="shadow-eligibility"
          style={{
            fontSize: "10px",
            fontWeight: 700,
            padding: "3px 8px",
            borderRadius: "3px",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            border: `1px solid ${eligible ? "#00FF8880" : "#8A8A93"}`,
            color:  eligible ? "#00FF88" : "#8A8A93",
          }}
        >
          {eligible ? "Eligible" : "Not eligible"}
        </span>
      </div>

      {!eligible && (
        <div data-testid="shadow-toggle-not-eligible"
              style={{ fontSize: "11px", color: "#8A8A93",
                       borderTop: "1px solid #26262B", paddingTop: "8px" }}>
          Shadow Mode is restricted to internal testers. Contact an
          administrator to be added to the allowlist.
        </div>
      )}

      {eligible && (
        <div style={{ display: "flex", alignItems: "center", gap: "10px",
                       borderTop: "1px solid #26262B", paddingTop: "10px" }}>
          <div style={{ fontSize: "12px", color: enabled ? "#00FF88" : "#8A8A93" }}
                data-testid="shadow-current-state">
            {enabled ? "Enabled — showing v2 shadow alongside v1"
                      : "Disabled — cockpit shows v1 only"}
          </div>
          <button
            data-testid="shadow-toggle-btn"
            onClick={toggle}
            disabled={busy}
            style={{
              marginLeft: "auto",
              background:   enabled ? "#7f1d1d" : "#00FF88",
              color:        enabled ? "#fff"    : "#08080A",
              border:       "0",
              borderRadius: "4px",
              padding:      "7px 14px",
              cursor:       busy ? "wait" : "pointer",
              fontSize:     "12px",
              fontWeight:   700,
            }}
          >
            {busy ? "Updating…"
                  : (enabled ? "Disable Shadow" : "Enable Shadow")}
          </button>
        </div>
      )}

      {err && (
        <div data-testid="shadow-toggle-error"
              style={{ color: "#fca5a5", fontSize: "11px" }}>{err}</div>
      )}
    </div>
  );
}

export default ReasonCodeChip;
